import { useState, useEffect, useRef } from "react";
import styles from "./admin-first-step.module.css";
import sportsIllustration from "illustrations/sports.png";
import { useTranslation } from "react-i18next";
import InvitationMail from "components/invitation-mail";
import instance from "services/api";
import { useLocation, useNavigate } from "react-router";
import tokenService from "services/token.service";
import Spinner from "components/functional/spinner";
import { ToastService, ToastComponent } from "services/toast.service";
import Disclaimer from "components/functional/disclaimer";
import useIsMobile from "hooks/detect-mobile-screen";
import { ClockAlert, Copy, CircleCheck } from "lucide-react";

const AdminFirstStep = () => {
  const [teamName, setTeamName] = useState("");
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [emailsList, setEmailsList] = useState([]);
  const [playerLink, setPlayerLink] = useState("");
  const [captainLink, setCaptainLink] = useState("");
  const [error, setError] = useState(false);

  const teamNameInputRef = useRef(null);
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(false);
  const [isSendingMails, setIsSendingMails] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const team_id = location.state?.team_id;

  const copyLink = (link, role) => {
    navigator.clipboard.writeText(link);
    ToastService.showSuccessToast(
      t("onboarding.admin.first-step.invitation-link.copy-cta")
    );
  };

  const fetchInvitationLinks = async () => {
    try {
      // Fetch player invitation link

      const playerResponse = await instance.post(
        "team-invitations/create-link",
        {
          role: "player",
          user_id: tokenService.getUserId(),
          team_id,
        }
      );
      setPlayerLink(playerResponse.data.link);

      // Fetch captain invitation link

      const captainResponse = await instance.post(
        "team-invitations/create-link",
        {
          role: "captain",
          user_id: tokenService.getUserId(),
          team_id,
        }
      );
      setCaptainLink(captainResponse.data.link);
    } catch (error) {
      console.error("Error fetching invitation links", error);
    }
  };

  const handleSendInvitations = async () => {
    setIsSendingMails(true);
    try {
      await instance.post("/team-invitations/send", {
        emails: emailsList,
        team_id,
        user_id: tokenService.getUserId(),
        organization_id: tokenService.getOrganizationId(),
      });
      setEmailsList([]);
      ToastService.showSuccessToast(
        t("onboarding.admin.first-step.invitation-mail.invited.sent")
      );
    } catch (error) {
      console.error("Error sending invitations", error);
    } finally {
      setIsSendingMails(false);
    }
  };

  const fetchTeamById = async () => {
    try {
      const response = await instance.get(`/teams/${team_id}`);
      setTeam(response.data);
    } catch (error) {
      console.error("Error fetching team by id", error);
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission or default behavior

      if (email) {
        setEmailsList((prevList) => {
          // Check if the email already exists in the list
          const emailExists = prevList.some((entry) => entry.email === email);
          if (emailExists) {
            return prevList; // Return the previous list if email already exists
          }
          return [
            ...prevList,
            {
              email,
              role: {
                name: t(
                  "onboarding.admin.first-step.invitation-mail.role.player"
                ),
                code: "player",
              },
            }, // Default role to "player"
          ];
        });
        setEmail(""); // Clear the email input field
      }
    }
  };

  const updateMyFirstTeam = async () => {
    if (!teamName) {
      setError(true);
      return;
    }

    setLoading(true);
    try {
      await instance.put(`teams/${team_id}`, {
        name: teamName,
        sport: "football",
      });
      await fetchTeamById();
    } catch (error) {
      console.error(error);
      ToastService.showErrorToast(
        t("onboarding.admin.first-step.create-team.error")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (email, newRole) => {
    setEmailsList((prevList) =>
      prevList.map((entry) =>
        entry.email === email ? { ...entry, role: newRole } : entry
      )
    );
  };

  const handleEmailRemoval = (email) => {
    setEmailsList((prevList) =>
      prevList.filter((entry) => entry.email !== email)
    );
  };

  useEffect(() => {
    fetchTeamById();
    fetchInvitationLinks();
    if (teamNameInputRef.current) {
      teamNameInputRef.current.focus();
    }
  }, []);

  if (!team_id) {
    return <Spinner />;
  }

  return (
    <div className={styles.wrapper}>
      <ToastComponent />
      <div className={styles.container}>
        {team?.name ? (
          <div className={styles.invitations} data-testid="invitations-section">
            {!isMobile && (
              <div className={styles.card}>
                <div className={styles["card-header"]}>
                  <h3 className="title-3">
                    {t(
                      "onboarding.admin.first-step.invitation-mail.card.title"
                    )}
                  </h3>
                  <p className="subtitle-1-variant">
                    {t(
                      "onboarding.admin.first-step.invitation-mail.card.subtitle"
                    )}
                  </p>
                </div>
                <div className={styles["card-content"]}>
                  <div className={styles["emails"]}>
                    <div className="form-group">
                      <h4 className="title-3-variant-low" htmlFor="email">
                        Email
                      </h4>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        className={styles["email-input"]}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={handleEmailKeyDown}
                        placeholder={t(
                          "onboarding.admin.first-step.invitation-mail.email.placeholder"
                        )}
                      />
                    </div>
                    <p
                      className={`subtitle-2-variant ${styles["email-subtitle"]}`}
                    >
                      {t(
                        "onboarding.admin.first-step.invitation-mail.email.description"
                      )}
                    </p>
                  </div>
                  <div className={`mb-12 ${styles["invitation-mails"]}`}>
                    <h4 className="title-3-variant-low invite-title">
                      {t(
                        "onboarding.admin.first-step.invitation-mail.invited.title"
                      )}{" "}
                      <span>({emailsList.length})</span>
                    </h4>
                    <div className={styles["invitation-list"]}>
                      {emailsList.length > 0 ? (
                        emailsList.map((item, index) => (
                          <InvitationMail
                            key={item.email}
                            index={index}
                            email={item.email}
                            role={item.role}
                            onRoleChange={handleRoleChange}
                            onEmailRemoval={handleEmailRemoval}
                          />
                        ))
                      ) : (
                        <p className="subtitle-2-variant">
                          {t(
                            "onboarding.admin.first-step.invitation-mail.invited.empty"
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn--primary"
                    disabled={emailsList.length === 0}
                    onClick={handleSendInvitations}
                  >
                    {emailsList.length > 0
                      ? t(
                          "onboarding.admin.first-step.invitation-mail.email.actions.send-plural",
                          {
                            count: emailsList.length,
                          }
                        )
                      : t(
                          "onboarding.admin.first-step.invitation-mail.email.actions.send"
                        )}
                    {isSendingMails && <Spinner></Spinner>}
                  </button>
                </div>
              </div>
            )}
            <div className={styles["actions-section"]}>
              <div className={styles["magic-links-container"]}>
                <div className={styles["links-header"]}>
                  <h3 className="title-3">
                    {t("onboarding.admin.first-step.invitation-link.title")}
                  </h3>
                  <div className={styles["links-disclaimer"]}>
                    <ClockAlert size={16} className={styles.icon} />
                    <p className="title-3-variant">
                      {t(
                        "onboarding.admin.first-step.invitation-link.disclaimer"
                      )}
                      <span className={styles.bold}>
                        {t(
                          "onboarding.admin.first-step.invitation-link.disclaimer-2"
                        )}
                      </span>
                    </p>
                  </div>
                </div>
                <div className={styles["links-container"]}>
                  <div
                    className={styles["link-card"]}
                    onClick={() => copyLink(playerLink, "player")}
                  >
                    <div className={styles["link-card-header"]}>
                      <p className="title-3">
                        {t(
                          "onboarding.admin.first-step.invitation-link.invite-title"
                        )}{" "}
                        <span className={styles["player-color"]}>
                          {t(
                            "onboarding.admin.first-step.invitation-link.player"
                          ).toLowerCase()}
                        </span>
                      </p>
                      <Copy size={16} className={styles["player-color"]} />
                    </div>
                    <p className="subtitle-2">{playerLink}</p>
                  </div>
                  <div
                    className={styles["link-card"]}
                    onClick={() => copyLink(captainLink, "captain")}
                  >
                    <div className={styles["link-card-header"]}>
                      <p className="title-3">
                        {t(
                          "onboarding.admin.first-step.invitation-link.invite-title"
                        )}{" "}
                        <span className={styles["captain-color"]}>
                          {t(
                            "onboarding.admin.first-step.invitation-link.captain"
                          ).toLowerCase()}
                        </span>
                      </p>
                      <Copy size={16} className={styles["captain-color"]} />
                    </div>
                    <p className="subtitle-2">{captainLink}</p>
                  </div>
                </div>
              </div>
              <div className={styles.next}>
                <div className={styles["next-card"]}>
                  <h3 className="title-3">
                    {t("onboarding.admin.first-step.next-step.title")}
                  </h3>
                  <div className={styles.reminder}>
                    <CircleCheck size={30} className="icon icon-success" />
                    <p className="subtitle-1">
                      {t("onboarding.admin.first-step.next-step.disclaimer")}
                    </p>
                  </div>
                  <button
                    data-testid="next-step-button"
                    className="btn btn--primary"
                    onClick={() => navigate("/dashboard")}
                  >
                    {t("onboarding.admin.first-step.next-step.cta")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <section className={styles["team-card"]}>
            <div className="form-group mb-32">
              <label htmlFor="teamName" className="required">
                {t("onboarding.admin.first-step.create-team.label")}
              </label>
              <input
                id="teamName"
                required
                type="text"
                placeholder={t(
                  "onboarding.admin.first-step.create-team.placeholder"
                )}
                className={`${error && "error"} ${styles.input}`}
                value={teamName}
                onChange={(e) => {
                  setTeamName(e.target.value);
                  setError(false);
                }}
                ref={teamNameInputRef}
              />
              {error && <p className="error-message">{t("errors.required")}</p>}
            </div>
            <div className={`${styles.sport} mb-24`}>
              <h3 className="title-3-variant-low">
                {t("onboarding.admin.first-step.create-team.sport")}
              </h3>
              <div className={styles["sport-list"]}>
                <Disclaimer status="soccer" />
              </div>
            </div>
            <div className={`${styles["illustration-container"]} mb-32`}>
              <div className="title-3-variant-low">
                {t("onboarding.admin.first-step.create-team.other-sports")}
              </div>
            </div>
            <div className={styles["create-team"]}>
              <button
                data-testid="update-team-button"
                className="btn btn--primary"
                onClick={updateMyFirstTeam}
                disabled={loading}
              >
                {t("onboarding.admin.first-step.create-team.cta")}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminFirstStep;
